import {
  type Address,
  decodeAbiParameters,
  type Hex,
  type PublicClient,
} from 'viem';
import { quoterV2Abi, uniswapV2Router02Abi, v4QuoterAbi } from '../../abis';
import { getAddresses } from '../../addresses';
import type { SupportedPublicClient } from '../../types';

/**
 * Unified Quoter for getting price quotes across Uniswap V2, V3, and V4
 *
 * This class provides a unified interface for price discovery across all
 * supported Uniswap versions, abstracting away the differences between
 * V2 router, V3 quoter, and V4 quoter contracts.
 */
export class Quoter {
  private publicClient: SupportedPublicClient;
  private chainId: number;
  private get rpc(): PublicClient {
    return this.publicClient as PublicClient;
  }

  constructor(publicClient: SupportedPublicClient, chainId: number) {
    this.publicClient = publicClient;
    this.chainId = chainId;
  }

  /**
   * Get a price quote for swapping an exact amount of input tokens on Uniswap V3
   * @param params Parameters for the quote
   * @returns Quote result including output amount and price impact
   */
  async quoteExactInputV3(params: {
    tokenIn: Address;
    tokenOut: Address;
    amountIn: bigint;
    fee: number;
    sqrtPriceLimitX96?: bigint;
  }): Promise<{
    amountOut: bigint;
    sqrtPriceX96After: bigint;
    initializedTicksCrossed: number;
    gasEstimate: bigint;
  }> {
    const addresses = getAddresses(this.chainId);

    try {
      const { result } = await this.rpc.simulateContract({
        address: addresses.v3Quoter,
        abi: quoterV2Abi,
        functionName: 'quoteExactInputSingle',
        args: [
          {
            tokenIn: params.tokenIn,
            tokenOut: params.tokenOut,
            amountIn: params.amountIn,
            fee: params.fee,
            sqrtPriceLimitX96: params.sqrtPriceLimitX96 ?? BigInt(0),
          },
        ],
      });
      return {
        amountOut: result[0],
        sqrtPriceX96After: result[1],
        initializedTicksCrossed: result[2],
        gasEstimate: result[3],
      };
    } catch (err: unknown) {
      const revertData: Hex | undefined =
        (err as { cause?: { data?: Hex }; data?: Hex })?.cause?.data ||
        (err as { data?: Hex }).data;
      if (
        revertData &&
        typeof revertData === 'string' &&
        revertData.startsWith('0x')
      ) {
        try {
          // Uniswap V3 QuoterV2 encodes (uint256 amount, uint160 sqrtPriceX96After, int24 tickAfter)
          const [amount, sqrtPriceX96After] = decodeAbiParameters(
            [{ type: 'uint256' }, { type: 'uint160' }, { type: 'int24' }],
            revertData as Hex,
          ) as unknown as [bigint, bigint, number];
          return {
            amountOut: amount,
            sqrtPriceX96After,
            initializedTicksCrossed: 0,
            gasEstimate: 0n,
          };
        } catch {
          // fall through
        }
      }
      throw err;
    }
  }

  /**
   * Get a price quote for receiving an exact amount of output tokens on Uniswap V3
   * @param params Parameters for the quote
   * @returns Quote result including required input amount
   */
  async quoteExactOutputV3(params: {
    tokenIn: Address;
    tokenOut: Address;
    amountOut: bigint;
    fee: number;
    sqrtPriceLimitX96?: bigint;
  }): Promise<{
    amountIn: bigint;
    sqrtPriceX96After: bigint;
    initializedTicksCrossed: number;
    gasEstimate: bigint;
  }> {
    const addresses = getAddresses(this.chainId);

    try {
      const { result } = await this.rpc.simulateContract({
        address: addresses.v3Quoter,
        abi: quoterV2Abi,
        functionName: 'quoteExactOutputSingle',
        args: [
          {
            tokenIn: params.tokenIn,
            tokenOut: params.tokenOut,
            amount: params.amountOut,
            fee: params.fee,
            sqrtPriceLimitX96: params.sqrtPriceLimitX96 ?? BigInt(0),
          },
        ],
      });
      return {
        amountIn: result[0],
        sqrtPriceX96After: result[1],
        initializedTicksCrossed: result[2],
        gasEstimate: result[3],
      };
    } catch (err: unknown) {
      const revertData: Hex | undefined =
        (err as { cause?: { data?: Hex }; data?: Hex })?.cause?.data ||
        (err as { data?: Hex }).data;
      if (
        revertData &&
        typeof revertData === 'string' &&
        revertData.startsWith('0x')
      ) {
        try {
          // Uniswap V3 QuoterV2 encodes (uint256 amount, uint160 sqrtPriceX96After, int24 tickAfter)
          const [amount, sqrtPriceX96After] = decodeAbiParameters(
            [{ type: 'uint256' }, { type: 'uint160' }, { type: 'int24' }],
            revertData as Hex,
          ) as unknown as [bigint, bigint, number];
          return {
            amountIn: amount,
            sqrtPriceX96After,
            initializedTicksCrossed: 0,
            gasEstimate: 0n,
          };
        } catch {
          // fall through
        }
      }
      throw err;
    }
  }

  /**
   * Get a price quote for swapping an exact amount of input tokens on Uniswap V2
   * @param params Parameters for the quote
   * @returns Array of amounts for each step in the path
   */
  async quoteExactInputV2(params: {
    amountIn: bigint;
    path: Address[];
  }): Promise<bigint[]> {
    const addresses = getAddresses(this.chainId);

    if (!addresses.univ2Router02) {
      throw new Error('Uniswap V2 Router not available on this chain');
    }

    const result = await this.rpc.readContract({
      address: addresses.univ2Router02,
      abi: uniswapV2Router02Abi,
      functionName: 'getAmountsOut',
      args: [params.amountIn, params.path],
    });

    return [...result]; // Convert readonly array to mutable array
  }

  /**
   * Get a price quote for receiving an exact amount of output tokens on Uniswap V2
   * @param params Parameters for the quote
   * @returns Array of amounts for each step in the path
   */
  async quoteExactOutputV2(params: {
    amountOut: bigint;
    path: Address[];
  }): Promise<bigint[]> {
    const addresses = getAddresses(this.chainId);

    if (!addresses.univ2Router02) {
      throw new Error('Uniswap V2 Router not available on this chain');
    }

    const result = await this.rpc.readContract({
      address: addresses.univ2Router02,
      abi: uniswapV2Router02Abi,
      functionName: 'getAmountsIn',
      args: [params.amountOut, params.path],
    });

    return [...result]; // Convert readonly array to mutable array
  }

  /**
   * Get a price quote for swapping an exact amount of input tokens on Uniswap V4
   * @param params Parameters for the quote
   * @returns Quote result for V4 pools
   */
  async quoteExactInputV4(params: {
    poolKey: {
      currency0: Address;
      currency1: Address;
      fee: number;
      tickSpacing: number;
      hooks: Address;
    };
    zeroForOne: boolean;
    exactAmount: bigint;
    hookData?: string;
  }): Promise<{
    amountOut: bigint;
    gasEstimate: bigint;
  }> {
    const addresses = getAddresses(this.chainId);

    const quoterAddress = addresses.uniswapV4Quoter;

    if (!quoterAddress) {
      throw new Error('No V4 quoter available on this chain');
    }

    const { result } = await this.rpc.simulateContract({
      address: quoterAddress,
      abi: v4QuoterAbi,
      functionName: 'quoteExactInputSingle',
      args: [
        {
          poolKey: params.poolKey,
          zeroForOne: params.zeroForOne,
          exactAmount: params.exactAmount,
          hookData: (params.hookData ?? '0x') as `0x${string}`,
        },
      ],
    });

    return {
      amountOut: result[0],
      gasEstimate: result[1],
    };
  }

  /**
   * Get a price quote for receiving an exact amount of output tokens on Uniswap V4
   * @param params Parameters for the quote
   * @returns Quote result for V4 pools
   */
  async quoteExactOutputV4(params: {
    poolKey: {
      currency0: Address;
      currency1: Address;
      fee: number;
      tickSpacing: number;
      hooks: Address;
    };
    zeroForOne: boolean;
    exactAmount: bigint;
    hookData?: string;
  }): Promise<{
    amountIn: bigint;
    gasEstimate: bigint;
  }> {
    const addresses = getAddresses(this.chainId);

    const quoterAddress = addresses.uniswapV4Quoter;

    if (!quoterAddress) {
      throw new Error('Uniswap V4 Quoter address not available on this chain');
    }

    const { result } = await this.rpc.simulateContract({
      address: quoterAddress,
      abi: v4QuoterAbi,
      functionName: 'quoteExactOutputSingle',
      args: [
        {
          poolKey: params.poolKey,
          zeroForOne: params.zeroForOne,
          exactAmount: params.exactAmount,
          hookData: (params.hookData ?? '0x') as `0x${string}`,
        },
      ],
    });

    return {
      amountIn: result[0],
      gasEstimate: result[1],
    };
  }

  /**
   * Quote a single exact-input trade using the Uniswap V4 Quoter (not Lens)
   * @param params Parameters for the quote
   */
  async quoteExactInputV4Quoter(params: {
    poolKey: {
      currency0: Address;
      currency1: Address;
      fee: number;
      tickSpacing: number;
      hooks: Address;
    };
    zeroForOne: boolean;
    exactAmount: bigint;
    hookData?: string;
  }): Promise<{
    amountOut: bigint;
    gasEstimate: bigint;
  }> {
    const addresses = getAddresses(this.chainId);
    const quoterAddress = addresses.uniswapV4Quoter;

    if (!quoterAddress) {
      throw new Error('Uniswap V4 Quoter address not available on this chain');
    }

    const { result } = await this.rpc.simulateContract({
      address: quoterAddress,
      abi: v4QuoterAbi,
      functionName: 'quoteExactInputSingle',
      args: [
        {
          poolKey: params.poolKey,
          zeroForOne: params.zeroForOne,
          exactAmount: params.exactAmount,
          hookData: (params.hookData ?? '0x') as `0x${string}`,
        },
      ],
    });

    return {
      amountOut: result[0],
      gasEstimate: result[1],
    };
  }

  /**
   * Quote a single exact-output trade using the Uniswap V4 Quoter (not Lens)
   * @param params Parameters for the quote
   */
  async quoteExactOutputV4Quoter(params: {
    poolKey: {
      currency0: Address;
      currency1: Address;
      fee: number;
      tickSpacing: number;
      hooks: Address;
    };
    zeroForOne: boolean;
    exactAmount: bigint;
    hookData?: string;
  }): Promise<{
    amountIn: bigint;
    gasEstimate: bigint;
  }> {
    const addresses = getAddresses(this.chainId);
    const quoterAddress = addresses.uniswapV4Quoter;

    if (!quoterAddress) {
      throw new Error('Uniswap V4 Quoter address not available on this chain');
    }

    const { result } = await this.rpc.simulateContract({
      address: quoterAddress,
      abi: v4QuoterAbi,
      functionName: 'quoteExactOutputSingle',
      args: [
        {
          poolKey: params.poolKey,
          zeroForOne: params.zeroForOne,
          exactAmount: params.exactAmount,
          hookData: (params.hookData ?? '0x') as `0x${string}`,
        },
      ],
    });

    return {
      amountIn: result[0],
      gasEstimate: result[1],
    };
  }
}
